/**
 * New Members (new-member outreach) client service.
 *
 * Reads go straight to Firestore (rules: leaders and admins). Every change goes
 * through a Cloud Function; the browser never writes outreach records.
 * Config and templates are the exception: admins write those directly.
 */
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
  serverTimestamp,
  deleteDoc,
  writeBatch,
} from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import type {
  ClaimOutreachResult,
  OutreachActivity,
  OutreachConfig,
  OutreachRecord,
  OutreachTemplate,
  QuietModeConfig,
  QuietScheduleResult,
  ResetOutreachResult,
} from '@/types/outreach';
import { STARTER_OUTREACH_TEMPLATES } from '@/data/outreachStarterTemplates';

const outreachPath = () => `artifacts/${appId}/outreach`;
const templatesPath = () => `artifacts/${appId}/outreach_templates`;
const configPath = () => `artifacts/${appId}/config/outreach`;

type Unsubscribe = () => void;

// ---------------------------------------------------------------------------
// Callables (lazy: keeps firebase/functions out of the first bundle)
// ---------------------------------------------------------------------------

async function callFunction<Req, Res>(name: string, data: Req): Promise<Res> {
  const [{ getFunctions, httpsCallable }, { app }] = await Promise.all([
    import('firebase/functions'),
    import('@/config/firebase'),
  ]);
  const callable = httpsCallable<Req & { appId: string }, Res>(getFunctions(app), name);
  const result = await callable({ ...data, appId });
  return result.data;
}

/** Say "I'm writing to them". Others see the card disabled until it expires or is sent. */
export function claimOutreach(memberId: string): Promise<ClaimOutreachResult> {
  return callFunction('claimOutreach', { memberId });
}

/** Leaders release their own claim; admins can release anyone's. */
export function releaseOutreachClaim(memberId: string): Promise<{ ok: true; released: boolean }> {
  return callFunction('releaseOutreachClaim', { memberId });
}

/** Admins only: put members (back) into New Members. */
export function resetOutreach(input: {
  memberIds: string[];
  override?: boolean;
  notify?: boolean;
  reason?: string;
}): Promise<ResetOutreachResult> {
  return callFunction('resetOutreach', input);
}

/** Accept the current acknowledgement text. */
export function acknowledgeOutreach(): Promise<{ ok: true; version: number }> {
  return callFunction('acknowledgeOutreach', {});
}

/** When is quiet time? Admins may pass unsaved settings to preview them. */
export function fetchQuietSchedule(
  quietMode?: QuietModeConfig,
  count = 4
): Promise<QuietScheduleResult> {
  return callFunction('quietModeSchedule', { quietMode, count });
}

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

/**
 * Members waiting for a hello. One equality filter, so it needs no composite index;
 * the list is small and sorted in the app.
 */
export function subscribeToWaitingMembers(
  callback: (records: OutreachRecord[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(collection(db, outreachPath()), where('stage', '==', 'new'));
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ ...(d.data() as OutreachRecord), memberId: d.id }))),
    (error) => {
      console.error('Failed to load waiting members:', error);
      onError?.(error);
    }
  );
}

/** Latest welcomes, visible to all leaders and admins ("who welcomed whom"). */
export function subscribeToRecentWelcomes(
  callback: (records: OutreachRecord[]) => void,
  max = 5
): Unsubscribe {
  const q = query(
    collection(db, outreachPath()),
    where('stage', '==', 'first_contacted'),
    orderBy('stageChangedAt', 'desc'),
    limit(max)
  );
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ ...(d.data() as OutreachRecord), memberId: d.id }))),
    (error) => {
      console.error('Failed to load recent welcomes:', error);
      callback([]);
    }
  );
}

/** Every record in one stage (admin desk tabs). */
export function subscribeToOutreachByStage(
  stage: OutreachRecord['stage'],
  callback: (records: OutreachRecord[]) => void,
  max = 200
): Unsubscribe {
  const q = query(
    collection(db, outreachPath()),
    where('stage', '==', stage),
    orderBy('stageChangedAt', 'desc'),
    limit(max)
  );
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ ...(d.data() as OutreachRecord), memberId: d.id }))),
    (error) => {
      console.error('Failed to load outreach records:', error);
      callback([]);
    }
  );
}

export function subscribeToOutreachActivity(
  memberId: string,
  callback: (events: OutreachActivity[]) => void
): Unsubscribe {
  const q = query(
    collection(db, `${outreachPath()}/${memberId}/activity`),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ ...(d.data() as OutreachActivity), id: d.id }))),
    () => callback([])
  );
}

export const DEFAULT_QUIET_MODE: QuietModeConfig = {
  enabled: true,
  cityName: 'Los Angeles, CA',
  latitude: 34.0522,
  longitude: -118.2437,
  timezone: 'America/Los_Angeles',
  minutesBeforeSunset: 18,
  minutesAfterSunset: 42,
  shabbat: true,
  yomTov: true,
  cholHamoed: false,
  fasts: false,
  heldMode: 'summary',
};

/** Config merged over safe defaults so the UI never sees missing fields. */
export const DEFAULT_OUTREACH_CONFIG: OutreachConfig = {
  enabled: false,
  claimTtlMinutes: 30,
  reminderAfterHours: 24,
  reminderRepeatHours: 24,
  maxReminders: 3,
  escalateAfterHours: 72,
  followUpAfterDays: 7,
  digestHourLocal: 9,
  timezone: 'America/Los_Angeles',
  personalLine: { required: true, minChars: 20 },
  acknowledgement: {
    text: [
      'Before you begin. Please:',
      '- Use what you see only to welcome and support members.',
      "- Don't share names or details outside the community.",
      '- Keep notes short and kind. Never record health, family, or money matters.',
      '- If someone asks not to be contacted, honor it.',
    ].join('\n'),
    version: 1,
  },
  quietMode: DEFAULT_QUIET_MODE,
};

export function mergeOutreachConfig(raw: Partial<OutreachConfig> | undefined): OutreachConfig {
  const d = DEFAULT_OUTREACH_CONFIG;
  const quietMode = { ...d.quietMode, ...(raw?.quietMode ?? {}) };
  if (!raw)
    return {
      ...d,
      personalLine: { ...d.personalLine },
      acknowledgement: { ...d.acknowledgement },
      quietMode,
    };
  return {
    ...d,
    ...raw,
    quietMode,
    enabled: raw.enabled === true,
    personalLine: { ...d.personalLine, ...(raw.personalLine ?? {}) },
    acknowledgement: { ...d.acknowledgement, ...(raw.acknowledgement ?? {}) },
  };
}

export function subscribeToOutreachConfig(callback: (config: OutreachConfig) => void): Unsubscribe {
  return onSnapshot(
    doc(db, configPath()),
    (snap) => callback(mergeOutreachConfig(snap.data() as Partial<OutreachConfig> | undefined)),
    (error) => {
      console.error('Failed to load outreach config:', error);
      callback(mergeOutreachConfig(undefined));
    }
  );
}

/** Admins only. Writes just the fields given. */
export async function saveOutreachConfig(
  patch: Partial<OutreachConfig>,
  adminId: string
): Promise<void> {
  await setDoc(
    doc(db, configPath()),
    { ...patch, updatedAt: serverTimestamp(), updatedBy: adminId },
    { merge: true }
  );
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export function subscribeToOutreachTemplates(
  callback: (templates: OutreachTemplate[]) => void
): Unsubscribe {
  return onSnapshot(
    collection(db, templatesPath()),
    (snap) => {
      const items = snap.docs
        .map((d) => ({ ...(d.data() as OutreachTemplate), id: d.id }))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      callback(items);
    },
    (error) => {
      console.error('Failed to load outreach templates:', error);
      callback([]);
    }
  );
}

export async function saveOutreachTemplate(
  template: Pick<
    OutreachTemplate,
    'title' | 'body' | 'language' | 'isActive' | 'sortOrder' | 'suggestWhenInviter'
  > & {
    id?: string;
  },
  adminId: string
): Promise<string> {
  const { id, ...rest } = template;
  const ref = id ? doc(db, templatesPath(), id) : doc(collection(db, templatesPath()));
  await setDoc(ref, { ...rest, updatedAt: serverTimestamp(), updatedBy: adminId }, { merge: true });
  return ref.id;
}

export async function deleteOutreachTemplate(templateId: string): Promise<void> {
  await deleteDoc(doc(db, templatesPath(), templateId));
}

/** Copy the built-in starter templates into Firestore so admins can edit them. */
export async function seedStarterTemplates(adminId: string): Promise<void> {
  const batch = writeBatch(db);
  for (const t of STARTER_OUTREACH_TEMPLATES) {
    const { id, ...rest } = t;
    batch.set(doc(db, templatesPath(), id.replace('starter-', 'seed-')), {
      ...rest,
      updatedAt: serverTimestamp(),
      updatedBy: adminId,
    });
  }
  await batch.commit();
}
